/**
 * CustomDatagrid Component
 *
 * A responsive data grid that automatically switches between:
 * - Desktop: Traditional table view with sorting/filtering
 * - Mobile: Card-based layout optimized for touch
 *
 * USAGE:
 * ```tsx
 * <List>
 *   <CustomDatagrid
 *     primarySource="name"      // Field for card title on mobile
 *     secondarySource="email"   // Field for subtitle on mobile
 *     detailColumns={2}         // Number of columns in mobile detail grid
 *   >
 *     <TextField source="name" />
 *     <TextField source="email" />
 *     <DateField source="createdAt" />
 *     <EditButton />
 *   </CustomDatagrid>
 * </List>
 * ```
 */

import React from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useListContext, useTranslate, DatagridProps, RaRecord } from 'react-admin';
import CustomDataGridDesktop from './CustomDataGridDesktop';
import CustomDataGridMobile from './CustomDataGridMobile';
import NoDataMessage from './NoDataMessage';

interface CustomDataGridProps<RecordType extends RaRecord = any> extends DatagridProps<RecordType> {
  children: React.ReactNode;
  /** Field used as big card title on mobile (e.g., "name") */
  primarySource?: string;
  /** Field used as subtitle under the title on mobile (e.g., "email") */
  secondarySource?: string;
  /** Number of columns for details on mobile (2 or 3) */
  detailColumns?: 2 | 3;
}

const CustomDatagrid = <RecordType extends RaRecord = any>({
  primarySource,
  secondarySource,
  detailColumns = 2,
  expand,
  ...props
}: CustomDataGridProps<RecordType>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data = [], isLoading } = useListContext();
  const translate = useTranslate();

  // Show empty state when no data
  if (!isLoading && data.length === 0) {
    return (
      <NoDataMessage
        title={translate('common.no_data_title')}
        description={translate('common.no_data_description')}
      />
    );
  }

  // Mobile view: Card-based layout
  if (isMobile) {
    return (
      <CustomDataGridMobile
        {...props}
        expand={expand}
        primarySource={primarySource}
        secondarySource={secondarySource}
        detailColumns={detailColumns}
      />
    );
  }

  // Desktop view: Traditional table
  return (
    <CustomDataGridDesktop
      {...props}
      expand={expand}
    />
  );
};

export default CustomDatagrid;
